/**
 * Guía: Documentación.
 *
 * Contenido derivado del capítulo **44** del manual: §0 (para qué está), §2
 * (qué se ingiere y qué no), §3 (quién ve qué), §4 (por qué el asistente puede
 * decir que no sabe) y §5 (qué se guarda de cada pregunta).
 *
 * Es la única guía que enseña una pantalla cuyo contenido **es el propio
 * manual**, así que tiene una responsabilidad de más: dejar claro que lo que
 * responde ahí no es un buscador de internet ni un modelo suelto, sino este
 * manual — y que cuando dice que no sabe, está diciendo la verdad.
 *
 * Los pasos van sin país: la documentación no depende del país seleccionado, y
 * por eso la entrada vive en la sección global del menú de los siete roles.
 */

import type { TourFlow } from '../types';

const DOCS_PATH = '/documentacion';

export const c44Documentacion: TourFlow = {
  id: 'admin.docs',
  chapter: '44',
  title: 'Documentación',
  summary:
    'Dónde está escrito cómo funciona la plataforma, y cómo preguntárselo al asistente sin perder tiempo.',
  href: DOCS_PATH,
  requiresCountry: false,
  estimatedMin: 3,
  steps: [
    {
      id: 'intro',
      title: 'La respuesta ya está escrita',
      body:
        'Casi todo lo que te preguntan un cliente o un comercio está documentado. El '
        + 'problema nunca es que falte: es saber en qué capítulo está. Esta pantalla '
        + 'existe para que no tengas que abrir un ticket interno ni interrumpir a un '
        + 'compañero para averiguarlo.',
      route: DOCS_PATH,
    },
    {
      id: 'chat',
      title: 'Pregunta con tus palabras',
      body:
        'No hace falta acertar con el término técnico. Escribe lo que te acaban de '
        + 'decir —«un cliente pagó en efectivo y reclama que no le dieron puntos»— y '
        + 'responde con eso. Enter envía; Shift+Enter salta de línea, para cuando '
        + 'quieras dar más contexto.',
      target: 'admin.docs.chat',
      placement: 'left',
    },
    {
      id: 'solo-el-manual',
      title: 'Responde sólo con el manual',
      body:
        'No consulta internet ni improvisa. Lee el manual de esta plataforma y cita el '
        + 'capítulo del que sale cada afirmación, para que puedas comprobarlo antes de '
        + 'repetírselo a alguien. Si algo no está documentado, te lo dice en vez de '
        + 'inventarlo — sobre pagos y reembolsos, una respuesta convincente y falsa te '
        + 'hace prometer algo que el sistema no va a cumplir.',
    },
    {
      id: 'huecos',
      title: 'Cuando dice que no sabe, sirve igual',
      body:
        'Esa pregunta queda registrada. Una que el asistente no supo responder es un '
        + 'hueco del manual señalado por quien lo necesitaba, y es así como se decide '
        + 'qué documentar después. Si te pasa, no es un fallo tuyo ni de la '
        + 'herramienta: has aportado algo.',
    },
    {
      id: 'indice',
      title: 'O busca el capítulo directamente',
      body:
        'A la izquierda están todos los capítulos, agrupados por dónde ocurren las '
        + 'cosas: los flujos primero —cómo funciona algo de punta a punta—, después '
        + 'este panel, el del comercio, la app del cliente y el sitio público. El '
        + 'buscador ignora los acentos: «liquidacion» encuentra lo mismo que '
        + '«liquidación».',
      target: 'admin.docs.index',
      placement: 'right',
    },
    {
      id: 'alcance',
      title: 'Sólo ves lo tuyo',
      body:
        'El índice está acotado a tu rol: aparecen los capítulos de las pantallas que '
        + 'tienes en el menú, más todo lo que describe el otro lado de la plataforma —lo '
        + 'que ve el comercio, lo que ve el cliente— porque eso lo necesitas para '
        + 'atender, sea cual sea tu rol. Si buscas algo y no aparece, puede ser que '
        + 'quede fuera de tu alcance.',
    },
    {
      id: 'compartir',
      title: 'Un capítulo se puede compartir',
      body:
        'Al abrir uno, la dirección del navegador cambia. Cópiala y pásasela a un '
        + 'compañero: le abrirá exactamente ese capítulo. Es más rápido que explicarle '
        + 'dónde mirar.',
      target: 'admin.docs.reader',
      optionalTarget: true,
    },
    {
      id: 'desde-el-ticket',
      title: 'Y se llega desde un ticket',
      body:
        'En el panel de cualquier ticket hay un botón que abre esta pantalla en otra '
        + 'pestaña, sin perder lo que llevabas escrito. Las preguntas que hagas desde '
        + 'ahí quedan ligadas a ese ticket, que es como sabemos si esto te está '
        + 'ahorrando trabajo de verdad.',
      roles: ['SUPER_ADMIN', 'SUPPORT_LEAD', 'SUPPORT_AGENT'],
    },
  ],
};
